import styled, { keyframes } from 'styled-components'

import { svgPaths } from '~/assets/images'
import { breakpoints, colors, spacings } from '~/assets/styles'

export const twinkleAnimation = keyframes`
  0%, 100% {
    opacity: 0.3;
    transform: scale(1);
  }
  50% {
    opacity: 1;
    transform: scale(1.2);
  }
`

const lightEvenTwinkleAnimation = keyframes`
  0%, 100% {
    box-shadow: -2px -1px 5px black, 0 0 0 0 white;
  }
  50% {
    box-shadow: -2px -1px 5px black, 0 0 6px 3px white;
  }
`

const lightOddTwinkleAnimation = keyframes`
  0%, 100% {
    box-shadow: -2px -1px 5px black, 0 0 6px 3px white;
  }
  50% {
    box-shadow: -2px -1px 5px black, 0 0 0 0 white;
  }
`

const fallingCoinsAnimation = keyframes`
  0% {
    opacity: 0;
    transform: translateY(-60%);
  }
  100% {
    opacity: 1;
  }
`

type BackgroundPropsType = {
  backgroundColor?: string
  backgroundDesktop?: string
  backgroundMobile?: string
  isThematicWheel?: boolean
}

export const Background = styled.div<BackgroundPropsType>`
  align-items: center;
  background: url(${({ backgroundMobile }) => backgroundMobile ?? svgPaths.backgroundMobile})
    no-repeat;
  background-color: ${({ backgroundColor }) => backgroundColor};
  background-position: center center;
  background-size: cover;
  display: flex;
  justify-content: center;
  min-height: 100%;
  overflow: hidden;
  padding-block: ${spacings[20]};

  @media (min-width: ${breakpoints.lg}px) {
    background-image: url(${({ backgroundDesktop }) => backgroundDesktop ?? svgPaths.background});
    background-size: ${({ isThematicWheel }) => (isThematicWheel ? 'cover' : 'auto')};
  }
`

export const Container = styled.div`
  align-items: center;
  display: grid;
  grid-template-areas:
    'header'
    'biggest-prize'
    'roulette'
    'shiny-coins';
  grid-template-columns: 1fr;
  grid-template-rows: auto;
  justify-items: center;
  max-width: 1280px;

  .header {
    grid-area: header;
  }

  .biggest-prize {
    grid-area: biggest-prize;
    margin-top: ${spacings[20]};
    margin-bottom: ${spacings[16]};
  }

  .roulette {
    grid-area: roulette;
    max-width: 100%;
    z-index: 2;

    .new-ring-light-container {
      height: 2.56877306%;
      left: 50%;
      position: absolute;
      top: 50%;
      transform: translate(-50%, -50%);
      width: 2.5754717%;

      .light {
        background: white;
        border-radius: 50%;
        height: 100%;
        position: absolute;
        width: 100%;
      }

      .even {
        animation: ${lightEvenTwinkleAnimation} 0.8s infinite ease-in-out;
      }

      .odd {
        animation: ${lightOddTwinkleAnimation} 0.8s infinite ease-in-out;
      }

      .light:nth-child(1) {
        transform: rotate(15deg) translate(2048%);
      }

      .light:nth-child(2) {
        transform: rotate(30deg) translate(2048%);
      }

      .light:nth-child(3) {
        transform: rotate(45deg) translate(2048%);
      }

      .light:nth-child(4) {
        transform: rotate(60deg) translate(2048%);
      }

      .light:nth-child(5) {
        transform: rotate(75deg) translate(2048%);
      }

      .light:nth-child(6) {
        transform: rotate(90deg) translate(2048%);
      }

      .light:nth-child(7) {
        transform: rotate(105deg) translate(2048%);
      }

      .light:nth-child(8) {
        transform: rotate(120deg) translate(2048%);
      }

      .light:nth-child(9) {
        transform: rotate(135deg) translate(2048%);
      }

      .light:nth-child(10) {
        transform: rotate(150deg) translate(2048%);
      }

      .light:nth-child(11) {
        transform: rotate(165deg) translate(2048%);
      }

      .light:nth-child(12) {
        transform: rotate(180deg) translate(2048%);
      }

      .light:nth-child(13) {
        transform: rotate(195deg) translate(2048%);
      }

      .light:nth-child(14) {
        transform: rotate(210deg) translate(2048%);
      }

      .light:nth-child(15) {
        transform: rotate(225deg) translate(2048%);
      }

      .light:nth-child(16) {
        transform: rotate(240deg) translate(2048%);
      }

      .light:nth-child(17) {
        transform: rotate(255deg) translate(2048%);
      }

      .light:nth-child(18) {
        transform: rotate(270deg) translate(2048%);
      }

      .light:nth-child(19) {
        transform: rotate(285deg) translate(2048%);
      }

      .light:nth-child(20) {
        transform: rotate(300deg) translate(2048%);
      }

      .light:nth-child(21) {
        transform: rotate(315deg) translate(2048%);
      }

      .light:nth-child(22) {
        transform: rotate(330deg) translate(2048%);
      }

      .light:nth-child(23) {
        transform: rotate(345deg) translate(2048%);
      }

      .light:nth-child(24) {
        transform: rotate(360deg) translate(2048%);
      }
    }
  }

  .falling-coins {
    animation: ${fallingCoinsAnimation} 1s ease-in-out 400ms forwards;
    opacity: 0;
  }

  .shiny-coins {
    grid-area: shiny-coins;
    margin-top: -54px;
    position: relative;

    .star-1,
    .star-2,
    .star-3 {
      animation: ${twinkleAnimation} 1s infinite ease-in-out;
      z-index: 1;
    }

    .star-1 {
      animation-delay: 0.1s;
    }

    .star-2 {
      animation-delay: 0.4s;
    }

    .star-3 {
      animation-delay: 0.7s;
    }

    &::after {
      background-color: ${colors.blue[800]};
      content: '';
      left: -2000px;
      position: absolute;
      top: 64%;
      width: 10000px;
      height: 5000px;
      z-index: 0;
    }
  }

  .roulette-container {
    & > div {
      height: 100%;
      position: relative;
      width: 100%;
    }

    &::after {
      background-color: white;
      border-radius: 50%;
      box-shadow: 2px 4px 20px -5px black;
      content: '';
      height: 8%;
      left: 50%;
      position: absolute;
      top: 50%;
      transform: translate(-50%, -50%);
      width: 8%;
      z-index: 5;
    }
  }

  button {
    background: linear-gradient(0deg, #1f7a2e -55.36%, #33cc4c 49.79%);
    border: 4px solid ${colors.white};
    border-radius: 100px;
    bottom: 36px;
    color: ${colors.white};
    position: absolute;
    box-shadow: 0 4px 4px 0 rgba(0, 0, 0, 0.25);
    transition:
      background 300ms,
      opacity 300ms,
      transform 300ms ease-in-out;
    width: 280px;
    z-index: 2;

    &:hover {
      background: linear-gradient(0deg, #1f7a2e -55.36%, #34b53b 49.79%);
    }

    &:disabled {
      opacity: 0;
      transform: scale(0.1);
    }
  }

  @media (min-width: ${breakpoints.lg}px) {
    grid-template-columns: repeat(2, 1fr);
    grid-template-areas:
      'header roulette'
      'shiny-coins roulette'
      'biggest-prize roulette';

    .biggest-prize {
      margin-bottom: 0;
    }

    .shiny-coins {
      margin-top: -30px;
    }
  }
`
